// Frontend default content (fallback / instant render). Mirrors backend defaults.
export const DEFAULT_CONTENT = {
  brand: { line1: "Odor Removal", line2: "DIRECTORY" },
  header: { cta: "Find a Specialist" },
  hero: {
    badge: "Independent Editorial Directory",
    title: "Compare odor removal methods & providers",
    subtitle: "Independent educational summaries of ozone, chlorine dioxide, neutralization and traditional cleaning, plus a verified provider directory.",
    primaryCta: "Compare Methods",
    secondaryCta: "Search Directory",
    image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
  },
  stats: [
    { value: "1,217+", label: "Directory Listings" },
    { value: "122+", label: "Cities Covered" },
    { value: "49+", label: "Odor Categories" },
    { value: "2,433+", label: "Monthly Visitors" },
  ],
  trustBadges: ["Independent Directory", "Verified Listings", "Editorial Comparison", "Updated Information", "Environmental Notes"],
  methodComparison: {
    eyebrow: "Method Comparison",
    title: "Independent Method Comparison",
    description: "Ten key criteria most useful to property owners. Select the info icon beside any category for a simple explanation.",
    disclaimer: "This directory provides educational comparisons based on publicly described service categories and odor removal technologies. Technology summaries are generalized and may not reflect every service offering of an individual company. Visitors should verify specific services directly with each provider.",
    cta: "See the full 27-category comparison",
  },
  greenCertified: {
    eyebrow: "Environmental Considerations",
    title: "Green-Certified Odor Removal",
    description: "A system or application process designed for effective odor neutralization while supporting environmentally responsible practices.",
    cardTitle: "Which methods are Green-Certified?",
    note: "This section is informational and reflects the process categories represented in the directory.",
  },
  editorsChoice: {
    eyebrow: "Editor's Analysis",
    title: "Editor's Choice",
    description: "An editorial recommendation selected on methodology, material safety, occupied-space usability, consistency, and environmental considerations.",
    badge: "Editor's Choice",
    greenLabel: "Green Certified",
    rating: "4.9",
    reviews: "287 reviews",
    providerName: "Master Odor Removal",
    methodLine: "Method: Proprietary Neutralization Process",
    body: "Master Odor Removal is positioned as a premium neutralization specialist. Rather than masking or oxidizing an odor, the process is designed to address the odor source in a way that is safe for sensitive respiratory conditions and interiors, including luxury homes, autos, yachts, and commercial spaces.",
    reasonsTitle: "How we made this selection",
    reasons: [
      "Direct liquid neutralization methodology",
      "Occupied space usability (residential, marine, automotive)",
      "Material safety across leather, wood, electronics",
      "Consistency across multiple property types",
    ],
    primaryCta: "Read full evaluation",
    secondaryCta: "Compare methods",
    image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  howItWorks: {
    eyebrow: "Scientific Summary",
    badge: "Editors Choice",
    title: "Why source-focused neutralization works",
    description: "A step-by-step look at how direct liquid neutralization addresses the odor at its origin.",
    steps: [
      { title: "Odor trapped", body: "Odor molecules embed in porous materials, ceilings, floors, walls, wood, upholstery, HVAC." },
      { title: "Direct liquid reaches source", body: "Treatment liquid is applied directly to the substrate holding the odor." },
      { title: "Molecular neutralization", body: "Active chemistry alters odor-causing molecules at their point of origin." },
      { title: "True neutral finish", body: "No masking. No oxidation of surrounding materials. No secondary smell." },
    ],
  },
  providerDirectory: { eyebrow: "Directory", title: "Browse listed providers", linkLabel: "Open full directory" },
  cities: { eyebrow: "National Coverage", title: "Providers by city", description: "Odor removal service coverage across the United States — browse by city." },
  categories: { eyebrow: "Directory Categories", title: "Odor Removal Company Directory", description: "Browse the directory by category to find the best odor removal companies across the United States." },
  ctaBand: {
    title: "Explore methods, then find a provider",
    description: "Compare science pages, then browse verified specialists filtered by odor, property type and environmental profile.",
    primaryCta: "Search Directory",
    secondaryCta: "Compare Methods",
  },
  faqHome: { eyebrow: "FAQ", title: "Frequently Asked Questions" },
  footer: {
    description: "An independent editorial directory comparing odor removal methods, technologies and verified providers.",
    disclaimer: "This directory provides educational comparisons based on publicly described service categories and odor removal technologies. Technology summaries are generalized and may not reflect every service offering of an individual company. Visitors should verify specific services directly with each provider.",
  },
  compare: {
    heroEyebrow: "Method Comparison",
    heroTitle: "Compare Odor Removal Methods",
    heroSubtitle: "Understand how each approach differs on effectiveness, safety, residue, and where it typically fits — so you can choose the right method for your property.",
    disclaimer: "This directory provides educational comparisons based on publicly described service categories and odor removal technologies. Technology summaries are generalized and may not reflect every service offering of an individual company. Visitors should verify specific services directly with each provider.",
  },
  providersPage: { heroEyebrow: "Directory", heroTitle: "Provider Directory", heroSubtitle: "Search providers in real time by company, city, ZIP, method, odor type or environmental certification." },
  locations: { heroEyebrow: "National Coverage", heroTitle: "Providers by city", heroSubtitle: "Odor removal service coverage across the United States — browse by city to find verified specialists near you." },
  resources: {
    heroEyebrow: "Resources",
    heroTitle: "Odor Removal Resources & Guides",
    heroSubtitle: "Educational guides, method explanations, and answers to the most common questions about professional odor removal.",
    evalEyebrow: "Independent Evaluation",
    evalTitle: "Understanding Odor Removal Technologies",
    evalDescription: "Educational summaries of the four primary odor removal approaches — how each works, where each excels, and its material and occupancy considerations.",
    faqTitle: "Frequently Asked Questions",
  },
  about: {
    heroEyebrow: "About",
    heroTitle: "About the Odor Removal Directory",
    heroSubtitle: "An independent editorial resource comparing odor removal methods, technologies, and verified providers across the United States.",
    missionEyebrow: "Our Mission",
    missionTitle: "Helping property owners choose the right method",
    missionP1: "Odor removal is not one-size-fits-all. Different technologies — molecular neutralization, ozone, chlorine dioxide, and traditional cleaning — vary widely on effectiveness, safety, residue, and where they fit. Our goal is to make those differences clear, then connect you with verified specialists.",
    missionP2: "All comparisons are based on publicly described service categories and available provider information. Consumers are encouraged to conduct additional research, including consulting independent sources and AI-powered resources such as ChatGPT, Google Gemini, and other AI search platforms, to better understand how different odor-removal systems work, their potential limitations and flaws, and which methods may be appropriate for a specific odor problem. Always verify specific services, treatment methods, safety considerations, guarantees, and qualifications directly with each provider before booking.",
    standEyebrow: "What We Stand For",
    pillars: [
      { title: "Independent & Editorial", desc: "Our comparisons are editorial. Listings and recommendations are not paid placements." },
      { title: "Verified Listings", desc: "Providers are organized by publicly described service categories and odor removal technologies." },
      { title: "Environmental Notes", desc: "We highlight environmental considerations such as gas-based vs. green-certified approaches." },
      { title: "Educational First", desc: "We explain how each method works so property owners can make informed decisions." },
    ],
    ctaTitle: "Start comparing methods and providers",
    ctaPrimary: "Search Directory",
    ctaSecondary: "Compare Methods",
  },
  faqs: [],
};
